import React from 'react';

/**
 * FestivalDecor — rich, theme-specific decorations layered over the public site.
 *
 * navratri : marigold toran across the top, garba dancers (chaniya choli + kediyu) with dandiya,
 *            glowing garbo (perforated clay lamp) and Mataji's trishul, swaying dandiya sticks.
 * holi     : Holika Dahan bonfire, big gulal colour patches, pichkaris (water guns) with colour jets,
 *            water balloons, drifting gulal specks.
 * diwali   : twinkling string lights, rockets climbing with sparkling bursts, a row of flickering diyas,
 *            a rangoli corner.
 * national days (gujarat_day / independence_day / republic_day): tricolour bunting.
 *
 * Everything is pure SVG/CSS (no image downloads), pointer-events: none, animates transform/opacity only,
 * and respects prefers-reduced-motion (see index.css .fest-* rules). Heavier pieces are hidden on phones.
 */

const Toran = () => (
  <svg className="fest-toran" viewBox="0 0 1200 70" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="marigold" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stopColor="#ffd25a" />
        <stop offset="70%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#c2410c" />
      </radialGradient>
    </defs>
    <path d="M0 6 Q300 60 600 6 T1200 6" fill="none" stroke="#7c2d12" strokeWidth="3" />
    {Array.from({ length: 24 }).map((_, i) => {
      const x = 25 + i * 50;
      const t = (x / 1200) * Math.PI * 2;
      const y = 6 + Math.abs(Math.sin(t)) * 50;
      return (
        <g key={i} transform={`translate(${x} ${y})`}>
          <path d="M-7 0 L0 22 L7 0 Z" fill="#15803d" opacity="0.9" />
          <path d="M-3 0 L0 26 L3 0 Z" fill="#166534" opacity="0.9" />
          <circle r="9" fill="url(#marigold)" />
          <circle r="4" fill="#fde68a" opacity="0.8" />
        </g>
      );
    })}
  </svg>
);

const Garbo = ({ className }) => (
  <svg className={className} viewBox="0 0 80 110" aria-hidden="true">
    <defs>
      <radialGradient id="garboGlow" cx="50%" cy="55%" r="60%">
        <stop offset="0%" stopColor="#fde047" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="40" cy="62" r="38" fill="url(#garboGlow)" className="fest-glow" />
    <path className="fest-flame" d="M40 8 C46 16 50 22 48 30 C46 36 34 36 32 30 C30 22 34 16 40 8 Z" fill="#fbbf24" />
    <path d="M28 40 Q40 32 52 40 L56 46 Q40 52 24 46 Z" fill="#7c2d12" />
    <path d="M22 48 Q40 40 58 48 C68 62 66 84 56 98 Q40 106 24 98 C14 84 12 62 22 48 Z" fill="#9a3412" />
    <path d="M22 48 Q40 40 58 48 C68 62 66 84 56 98 Q40 106 24 98 C14 84 12 62 22 48 Z" fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.7" />
    {[[40, 60], [30, 68], [50, 68], [40, 78], [28, 84], [52, 84], [40, 92]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.2" fill="#fde047" className="fest-twinkle" style={{ animationDelay: `${i * 0.25}s` }} />
    ))}
  </svg>
);

const Trishul = ({ className }) => (
  <svg className={className} viewBox="0 0 60 120" aria-hidden="true">
    <path d="M30 118 V40" stroke="#b45309" strokeWidth="4" strokeLinecap="round" />
    <path d="M30 40 V10 M14 46 C10 30 14 18 20 10 M46 46 C50 30 46 18 40 10" fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
    <path d="M14 46 Q30 54 46 46" fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
    <circle cx="30" cy="62" r="6" fill="#dc2626" />
    <path d="M22 70 Q30 78 38 70" fill="none" stroke="#fbbf24" strokeWidth="3" />
  </svg>
);

const Dandiya = ({ className, flip }) => (
  <svg className={className} viewBox="0 0 90 90" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
    <g className="fest-sway">
      <rect x="10" y="40" width="70" height="7" rx="3.5" transform="rotate(-35 45 45)" fill="#b91c1c" />
      <rect x="10" y="40" width="70" height="7" rx="3.5" transform="rotate(-35 45 45)" fill="none" stroke="#fbbf24" strokeWidth="2" strokeDasharray="8 8" />
    </g>
    <g className="fest-sway-rev">
      <rect x="10" y="40" width="70" height="7" rx="3.5" transform="rotate(35 45 45)" fill="#15803d" />
      <rect x="10" y="40" width="70" height="7" rx="3.5" transform="rotate(35 45 45)" fill="none" stroke="#fbbf24" strokeWidth="2" strokeDasharray="8 8" />
    </g>
    <circle cx="45" cy="45" r="5" fill="#fbbf24" />
  </svg>
);

/* Shared palette for the garba dancers — folk-art silhouettes with rich garments */
const SKIN = '#4a1d15';
const MAROON = '#8f1d1d';
const RED = '#c62828';
const GREEN = '#1b7f3b';
const GOLD = '#f2b632';
const CREAM = '#fbf3e0';
const SAFFRON = '#f27c1b';
const TEAL = '#0e7490';

/* Mirror-work dots placed along a quadratic hem curve */
const hemDots = (x0, y0, cx, cy, x1, y1, n, r = 2.4, fill = CREAM, stroke = TEAL) =>
  Array.from({ length: n }).map((_, i) => {
    const t = (i + 0.5) / n;
    const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * x1;
    const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * y1;
    return <circle key={i} cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth="1" />;
  });

/* Garba dancer — chaniya choli with mirror-work, flowing odhani, bangles, dandiya, mid-twirl */
const DancerChaniya = ({ className }) => (
  <svg className={className} viewBox="0 0 170 230" aria-hidden="true">
    <defs>
      <linearGradient id="chaniyaGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={MAROON} />
        <stop offset="100%" stopColor={RED} />
      </linearGradient>
      <linearGradient id="odhaniGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={SAFFRON} />
        <stop offset="100%" stopColor="#f9a825" />
      </linearGradient>
    </defs>
    <g className="fest-dance">
      {/* odhani (dupatta) flowing behind, from head over the left shoulder */}
      <path d="M84 30 C60 40 40 70 30 110 C24 134 30 160 22 186 C40 176 52 150 58 124 C64 100 76 70 96 48 Z" fill="url(#odhaniGrad)" opacity="0.95" />
      <path d="M30 110 C24 134 30 160 22 186" fill="none" stroke={GOLD} strokeWidth="2.5" />
      {/* braid falling down the back (behind the body) */}
      <path d="M96 40 C112 56 112 80 120 96" fill="none" stroke={SKIN} strokeWidth="4" strokeLinecap="round" />
      <circle cx="121" cy="99" r="3" fill={RED} />
      {/* chaniya (skirt) in a twirl */}
      <path d="M70 92 Q84 88 98 92 C118 128 146 168 142 206 Q84 226 24 206 C20 168 50 128 70 92 Z" fill="url(#chaniyaGrad)" />
      {/* decorative bands following the hem */}
      <path d="M26 200 Q84 220 140 200" fill="none" stroke={GOLD} strokeWidth="5" />
      <path d="M30 190 Q84 210 138 190" fill="none" stroke={GREEN} strokeWidth="6" />
      <path d="M34 179 Q84 198 134 179" fill="none" stroke={GOLD} strokeWidth="2" />
      {hemDots(34, 179, 84, 198, 134, 179, 9, 2.6)}
      <path d="M42 160 Q84 176 126 160" fill="none" stroke={GOLD} strokeWidth="2" strokeDasharray="1 5" />
      {hemDots(50, 140, 84, 152, 118, 140, 6, 2.2, GOLD, MAROON)}
      {hemDots(60, 118, 84, 126, 108, 118, 4, 2, GOLD, MAROON)}
      {/* choli (blouse) */}
      <path d="M64 56 Q84 62 104 56 L108 74 Q84 82 60 74 Z" fill={GREEN} />
      <path d="M64 56 Q84 62 104 56" fill="none" stroke={GOLD} strokeWidth="2.5" />
      <path d="M60 74 Q84 82 108 74" fill="none" stroke={GOLD} strokeWidth="2.5" />
      {hemDots(66, 66, 84, 72, 102, 66, 4, 2, CREAM, TEAL)}
      {/* waist / midriff */}
      <path d="M70 74 Q84 80 98 74 L98 92 Q84 88 70 92 Z" fill={SKIN} />
      <path d="M68 90 Q84 96 100 90" fill="none" stroke={GOLD} strokeWidth="3" />
      {/* neck + head with hair bun, maang tikka and earrings */}
      <rect x="78" y="42" width="12" height="16" rx="5" fill={SKIN} />
      <circle cx="84" cy="32" r="13" fill={SKIN} />
      <circle cx="98" cy="26" r="6" fill={SKIN} />
      <circle cx="98" cy="26" r="6" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="2 2" />
      <path d="M84 19 L84 24" stroke={GOLD} strokeWidth="2" strokeLinecap="round" />
      <circle cx="84" cy="25" r="1.8" fill={GOLD} />
      <circle cx="72" cy="36" r="1.8" fill={GOLD} />
      <path d="M72 38 L72 43" stroke={GOLD} strokeWidth="1.5" />
      {/* arms — right raised with dandiya, left extended gracefully */}
      <path d="M104 62 C118 52 130 40 140 26" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M64 62 C50 68 34 78 18 86" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {/* bangles */}
      {[[130, 36], [126, 40], [122, 44]].map(([x, y], i) => <path key={i} d={`M${x - 4} ${y - 4} L${x + 4} ${y + 4}`} stroke={i === 1 ? GREEN : GOLD} strokeWidth="2.5" strokeLinecap="round" />)}
      {[[28, 80], [32, 78], [36, 76]].map(([x, y], i) => <path key={i} d={`M${x - 2} ${y - 5} L${x + 2} ${y + 5}`} stroke={i === 1 ? GREEN : GOLD} strokeWidth="2.5" strokeLinecap="round" />)}
      {/* dandiya sticks */}
      <g transform="rotate(-38 140 26)">
        <rect x="112" y="23" width="56" height="6" rx="3" fill={RED} />
        <rect x="112" y="23" width="56" height="6" rx="3" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="6 6" />
      </g>
      <g transform="rotate(20 18 86)">
        <rect x="-10" y="83" width="56" height="6" rx="3" fill={GREEN} />
        <rect x="-10" y="83" width="56" height="6" rx="3" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="6 6" />
      </g>
      {/* foot with payal (anklet) peeking from the twirl */}
      <ellipse cx="56" cy="214" rx="10" ry="4.5" fill={SKIN} />
      <path d="M48 211 Q56 208 64 211" fill="none" stroke={GOLD} strokeWidth="1.5" />
    </g>
  </svg>
);

/* Garba dancer — kediyu (frilled top) with embroidery, pagdi with kalgi, dhoti in a dance step, dandiya */
const DancerKediyu = ({ className }) => (
  <svg className={className} viewBox="0 0 170 230" aria-hidden="true">
    <defs>
      <linearGradient id="kediyuGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fffaf0" />
        <stop offset="100%" stopColor={CREAM} />
      </linearGradient>
    </defs>
    <g className="fest-dance-rev">
      {/* legs — dhoti (chorno): left standing, right lifted in a step; mojari shoes */}
      <path d="M74 132 L66 200" stroke="#f1e4c8" strokeWidth="18" strokeLinecap="round" />
      <path d="M96 132 L116 160 L110 196" fill="none" stroke="#f1e4c8" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M74 132 L66 200 M96 132 L116 160 L110 196" fill="none" stroke="#d6c39b" strokeWidth="1.5" strokeDasharray="3 6" />
      <path d="M74 132 L66 200 M96 132 L116 160 L110 196" fill="none" stroke="#c9b48a" strokeWidth="0.8" />
      <path d="M66 200 L64 210" stroke={SKIN} strokeWidth="9" strokeLinecap="round" />
      <path d="M110 196 L112 206" stroke={SKIN} strokeWidth="9" strokeLinecap="round" />
      <path d="M52 216 Q60 206 74 212 L76 218 Z" fill={RED} />
      <path d="M100 210 Q112 202 124 210 L122 216 Z" fill={RED} />
      <path d="M52 216 Q60 206 74 212 M100 210 Q112 202 124 210" fill="none" stroke={GOLD} strokeWidth="1.2" />
      {/* kediyu — fitted bodice then gathered frills */}
      <path d="M62 60 Q85 66 108 60 L112 96 Q85 104 58 96 Z" fill="url(#kediyuGrad)" />
      <path d="M58 96 Q85 104 112 96 L124 136 Q85 150 46 136 Z" fill="url(#kediyuGrad)" />
      {[54, 63, 72, 81, 90, 99, 108, 117].map((x, i) => (
        <path key={i} d={`M${x - (x - 85) * 0.12} 100 Q${x} 118 ${x + (x - 85) * 0.1} 136`} fill="none" stroke="#d9c7a1" strokeWidth="1.3" />
      ))}
      <path d="M46 136 Q85 150 124 136" fill="none" stroke={RED} strokeWidth="3" />
      <path d="M48 131 Q85 145 122 131" fill="none" stroke={GREEN} strokeWidth="1.5" strokeDasharray="2 4" />
      {/* embroidered yoke + mirror-work */}
      <path d="M72 60 Q85 66 98 60 L96 84 Q85 88 74 84 Z" fill={RED} />
      <path d="M72 60 Q85 66 98 60 L96 84 Q85 88 74 84 Z" fill="none" stroke={GOLD} strokeWidth="1.5" />
      {hemDots(76, 72, 85, 76, 94, 72, 3, 2.2, CREAM, TEAL)}
      <path d="M85 66 L85 84" stroke={GOLD} strokeWidth="1.5" />
      {/* kamarband (sash) with hanging tassel */}
      <path d="M60 94 Q85 102 110 94" fill="none" stroke={RED} strokeWidth="6" />
      <path d="M62 94 Q85 101 108 94" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="2 4" />
      <path d="M100 98 L104 118" stroke={GOLD} strokeWidth="2" />
      <circle cx="104" cy="121" r="3" fill={GREEN} />
      {/* neck + head with moustache line, earring */}
      <rect x="78" y="46" width="14" height="16" rx="6" fill={SKIN} />
      <circle cx="85" cy="34" r="13" fill={SKIN} />
      <circle cx="74" cy="38" r="1.6" fill={GOLD} />
      {/* pagdi (turban) with gold bands and a kalgi feather */}
      <path d="M68 30 C68 14 102 14 102 30 Q85 24 68 30 Z" fill={RED} />
      <path d="M69 26 Q85 18 101 26" fill="none" stroke={GOLD} strokeWidth="2.5" />
      <path d="M72 22 Q85 15 98 22" fill="none" stroke={GOLD} strokeWidth="1.5" />
      <path d="M100 22 C108 16 110 8 106 2" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" />
      <circle cx="106" cy="3" r="2.5" fill={GOLD} />
      {/* arms — left raised with dandiya, right forward */}
      <path d="M64 66 C50 56 40 44 30 28" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M106 66 C120 72 136 80 152 88" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {/* dandiya sticks */}
      <g transform="rotate(38 30 28)">
        <rect x="2" y="25" width="56" height="6" rx="3" fill={GREEN} />
        <rect x="2" y="25" width="56" height="6" rx="3" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="6 6" />
      </g>
      <g transform="rotate(-20 152 88)">
        <rect x="124" y="85" width="56" height="6" rx="3" fill={RED} />
        <rect x="124" y="85" width="56" height="6" rx="3" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="6 6" />
      </g>
    </g>
  </svg>
);

/* ---------------- HOLI ---------------- */
const Bonfire = ({ className }) => (
  <svg className={className} viewBox="0 0 160 150" aria-hidden="true">
    <defs>
      <radialGradient id="fireGlow" cx="50%" cy="70%" r="55%">
        <stop offset="0%" stopColor="#fb923c" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#fb923c" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="flameA" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#dc2626" />
        <stop offset="55%" stopColor="#f97316" />
        <stop offset="100%" stopColor="#fde047" />
      </linearGradient>
    </defs>
    <ellipse cx="80" cy="95" rx="78" ry="60" fill="url(#fireGlow)" className="fest-glow" />
    <g transform="translate(80 128)">
      <rect x="-48" y="-6" width="96" height="10" rx="5" transform="rotate(-14)" fill="#7c2d12" />
      <rect x="-48" y="-6" width="96" height="10" rx="5" transform="rotate(14)" fill="#92400e" />
      <rect x="-40" y="-2" width="80" height="9" rx="4.5" fill="#78350f" />
    </g>
    <path className="fest-flame" style={{ transformOrigin: '80px 125px' }} d="M80 30 C96 52 112 70 108 96 C104 118 60 120 54 96 C50 74 66 56 80 30 Z" fill="url(#flameA)" />
    <path className="fest-flame-rev" style={{ transformOrigin: '80px 125px' }} d="M80 62 C90 74 98 86 96 100 C94 112 66 114 64 100 C62 88 72 76 80 62 Z" fill="#fde047" opacity="0.9" />
    {[[40, 40], [118, 34], [30, 80], [126, 76], [70, 14], [100, 20]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="2.2" fill="#fde68a" className="fest-spark" style={{ animationDelay: `${i * 0.4}s` }} />
    ))}
  </svg>
);

const Pichkari = ({ className, color = '#db2777', flip }) => (
  <svg className={className} viewBox="0 0 200 110" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
    {/* colour jet */}
    <path className="fest-jet" d="M120 40 Q160 20 196 12" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray="14 10" opacity="0.85" />
    {[[150, 26], [170, 18], [188, 12], [162, 34], [182, 26]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={3 + (i % 2)} fill={color} className="fest-spark" style={{ animationDelay: `${i * 0.3}s` }} />
    ))}
    {/* body */}
    <rect x="30" y="34" width="86" height="26" rx="13" fill="#0ea5e9" />
    <rect x="30" y="34" width="86" height="26" rx="13" fill="none" stroke="#0369a1" strokeWidth="3" />
    <rect x="42" y="40" width="60" height="6" rx="3" fill="#bae6fd" opacity="0.9" />
    <rect x="112" y="41" width="14" height="12" rx="3" fill="#facc15" />
    {/* plunger */}
    <rect x="6" y="43" width="28" height="8" rx="4" fill="#fbbf24" />
    <rect x="2" y="36" width="10" height="22" rx="3" fill="#ef4444" />
    {/* handle + tank */}
    <path d="M60 60 L54 96 H72 L76 60 Z" fill="#22c55e" />
    <ellipse cx="90" cy="76" rx="16" ry="14" fill={color} opacity="0.9" />
    <ellipse cx="90" cy="76" rx="16" ry="14" fill="none" stroke="#7e22ce" strokeWidth="2" />
  </svg>
);

const Balloon = ({ className, color }) => (
  <svg className={className} viewBox="0 0 40 52" aria-hidden="true">
    <g className="fest-float">
      <path d="M20 2 C34 2 38 20 30 34 C26 41 14 41 10 34 C2 20 6 2 20 2 Z" fill={color} />
      <path d="M13 12 Q16 6 22 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
      <path d="M17 41 L20 48 L23 41 Z" fill={color} />
    </g>
  </svg>
);

/* Colour splashes on white Holi clothes */
const Splashes = ({ spots }) => spots.map(([x, y, r, c], i) => (
  <g key={i}>
    <circle cx={x} cy={y} r={r} fill={c} opacity="0.85" />
    <circle cx={x + r * 0.9} cy={y - r * 0.6} r={r * 0.35} fill={c} opacity="0.8" />
    <circle cx={x - r * 0.8} cy={y + r * 0.7} r={r * 0.28} fill={c} opacity="0.8" />
  </g>
));

/* Puff of gulal thrown into the air (animates upward and fades) */
const GulalPuff = ({ x, y, color, delay = 0 }) => (
  <g className="fest-puff" style={{ transformOrigin: `${x}px ${y}px`, animationDelay: `${delay}s` }}>
    <circle cx={x} cy={y} r="10" fill={color} opacity="0.55" />
    <circle cx={x - 9} cy={y + 4} r="6" fill={color} opacity="0.5" />
    <circle cx={x + 9} cy={y + 3} r="7" fill={color} opacity="0.5" />
    <circle cx={x + 2} cy={y - 10} r="5" fill={color} opacity="0.45" />
    {[[-16, -4], [14, -12], [-6, -16], [18, 6], [-20, 8]].map(([dx, dy], i) => (
      <circle key={i} cx={x + dx} cy={y + dy} r="1.8" fill={color} className="fest-spark" style={{ animationDelay: `${delay + i * 0.25}s` }} />
    ))}
  </g>
);

/* Holi woman — white salwar-kameez splashed with colour, pink dupatta, throwing gulal, thali of colours */
const HoliWoman = ({ className }) => (
  <svg className={className} viewBox="0 0 170 230" aria-hidden="true">
    <g className="fest-dance">
      {/* dupatta flowing behind */}
      <path d="M96 52 C120 70 132 100 128 140 C126 160 134 176 130 196 C118 180 110 160 112 136 C114 108 104 82 84 62 Z" fill="#ec4899" opacity="0.9" />
      <path d="M128 140 C126 160 134 176 130 196" fill="none" stroke={GOLD} strokeWidth="2" />
      {/* salwar (legs) */}
      <path d="M70 150 L62 210" stroke="#f8f4ea" strokeWidth="17" strokeLinecap="round" />
      <path d="M98 150 L106 210" stroke="#f8f4ea" strokeWidth="17" strokeLinecap="round" />
      <path d="M62 210 L60 218 M106 210 L108 218" stroke={SKIN} strokeWidth="9" strokeLinecap="round" />
      <path d="M50 222 Q60 214 72 220 L72 226 Z M96 220 Q108 214 118 222 L118 226 Z" fill="#a21caf" />
      {/* kameez (long tunic) */}
      <path d="M62 60 Q84 66 106 60 C108 84 104 100 108 118 L118 158 Q84 168 50 158 L60 118 C64 100 60 84 62 60 Z" fill="#fffaf2" />
      <path d="M50 158 Q84 168 118 158" fill="none" stroke="#ec4899" strokeWidth="3" />
      <path d="M62 60 L52 78 L64 84 Z M106 60 L116 78 L104 84 Z" fill="#fffaf2" />
      <path d="M52 78 L64 84 M116 78 L104 84" stroke="#ec4899" strokeWidth="2" />
      <path d="M62 60 Q84 66 106 60" fill="none" stroke="#22c55e" strokeWidth="2.5" />
      <Splashes spots={[[72, 84, 9, '#22c55e'], [100, 100, 11, '#f472b6'], [80, 128, 10, '#3b82f6'], [106, 140, 7, '#facc15'], [64, 112, 6, '#a855f7'], [92, 74, 5, '#f97316']]} />
      {/* neck + head, braid, earring, bindi + colour on the cheek */}
      <rect x="78" y="42" width="12" height="16" rx="5" fill={SKIN} />
      <circle cx="84" cy="32" r="13" fill={SKIN} />
      <path d="M94 38 C110 52 108 76 114 92" fill="none" stroke={SKIN} strokeWidth="4" strokeLinecap="round" />
      <circle cx="115" cy="95" r="3" fill="#ec4899" />
      <circle cx="84" cy="25" r="1.8" fill="#dc2626" />
      <circle cx="73" cy="36" r="1.6" fill={GOLD} />
      <circle cx="92" cy="36" r="4" fill="#f472b6" opacity="0.85" />
      <circle cx="76" cy="30" r="3" fill="#22c55e" opacity="0.8" />
      {/* right arm raised, throwing gulal; left arm holding a thali of colours */}
      <path d="M104 64 C118 52 128 40 136 24" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M64 66 C52 76 40 86 30 96" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {[[126, 36], [122, 41]].map(([x, y], i) => <path key={i} d={`M${x - 4} ${y - 4} L${x + 4} ${y + 4}`} stroke={i ? '#22c55e' : GOLD} strokeWidth="2.5" strokeLinecap="round" />)}
      <GulalPuff x={140} y={14} color="#f472b6" />
      {/* thali (plate) with mounds of gulal */}
      <ellipse cx="26" cy="100" rx="18" ry="6" fill={GOLD} />
      <ellipse cx="26" cy="99" rx="14" ry="4" fill="#fde68a" />
      <path d="M14 98 Q19 88 24 98 Z" fill="#22c55e" />
      <path d="M22 98 Q27 86 32 98 Z" fill="#f472b6" />
      <path d="M30 98 Q35 89 40 98 Z" fill="#3b82f6" />
    </g>
  </svg>
);

/* Holi man — white kurta-pyjama splashed with colour, aiming a pichkari with both hands */
const HoliMan = ({ className }) => (
  <svg className={className} viewBox="0 0 200 230" aria-hidden="true">
    <g className="fest-dance-rev">
      {/* pyjama legs in a playful stance */}
      <path d="M74 150 L58 208" stroke="#f8f4ea" strokeWidth="17" strokeLinecap="round" />
      <path d="M98 150 L112 208" stroke="#f8f4ea" strokeWidth="17" strokeLinecap="round" />
      <path d="M58 208 L54 216 M112 208 L116 216" stroke={SKIN} strokeWidth="9" strokeLinecap="round" />
      <path d="M40 220 Q52 212 66 218 L66 224 Z M104 218 Q118 212 130 220 L130 224 Z" fill="#1d4ed8" />
      {/* kurta */}
      <path d="M60 60 Q86 66 112 60 C114 84 110 100 114 118 L122 154 Q86 164 50 154 L58 118 C62 100 58 84 60 60 Z" fill="#fffaf2" />
      <path d="M50 154 Q86 164 122 154" fill="none" stroke="#3b82f6" strokeWidth="3" />
      <path d="M60 60 L48 80 L62 86 Z M112 60 L124 80 L110 86 Z" fill="#fffaf2" />
      <path d="M48 80 L62 86 M124 80 L110 86" stroke="#3b82f6" strokeWidth="2" />
      <path d="M80 62 L80 98 L92 98 L92 62" fill="none" stroke="#3b82f6" strokeWidth="1.5" />
      <circle cx="86" cy="72" r="1.5" fill="#3b82f6" /><circle cx="86" cy="82" r="1.5" fill="#3b82f6" /><circle cx="86" cy="92" r="1.5" fill="#3b82f6" />
      <Splashes spots={[[68, 88, 10, '#f472b6'], [104, 84, 8, '#facc15'], [88, 120, 12, '#22c55e'], [62, 132, 7, '#a855f7'], [110, 132, 8, '#f97316'], [86, 70, 5, '#3b82f6']]} />
      {/* neck + head, cap of colour, colour on the face, wide grin */}
      <rect x="80" y="44" width="14" height="16" rx="6" fill={SKIN} />
      <circle cx="87" cy="34" r="13" fill={SKIN} />
      <path d="M74 30 C76 16 98 14 100 30 Q87 26 74 30 Z" fill="#facc15" />
      <circle cx="80" cy="36" r="3.5" fill="#22c55e" opacity="0.85" />
      <circle cx="95" cy="38" r="3" fill="#f472b6" opacity="0.85" />
      <path d="M80 42 Q87 47 94 42" fill="none" stroke="#fde68a" strokeWidth="1.6" strokeLinecap="round" />
      {/* arms holding the pichkari — both forward-up */}
      <path d="M108 66 C122 60 134 54 146 52" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M66 68 C86 66 108 62 130 58" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {/* pichkari */}
      <g transform="translate(150 50) rotate(-22) scale(0.78) translate(-150 -50)">
        <rect x="112" y="40" width="70" height="20" rx="10" fill="#0ea5e9" />
        <rect x="112" y="40" width="70" height="20" rx="10" fill="none" stroke="#0369a1" strokeWidth="2.5" />
        <rect x="122" y="45" width="46" height="4" rx="2" fill="#bae6fd" />
        <rect x="180" y="45" width="10" height="10" rx="2" fill="#facc15" />
        <rect x="96" y="47" width="18" height="6" rx="3" fill={GOLD} />
        <rect x="92" y="42" width="8" height="16" rx="2" fill="#ef4444" />
        <path d="M140 60 L136 84 H152 L154 60 Z" fill="#22c55e" />
        <ellipse cx="160" cy="76" rx="13" ry="11" fill="#db2777" />
        <ellipse cx="160" cy="76" rx="13" ry="11" fill="none" stroke="#7e22ce" strokeWidth="1.5" />
        {/* jet */}
        <path className="fest-jet" d="M192 50 Q210 36 232 30" fill="none" stroke="#db2777" strokeWidth="6" strokeLinecap="round" strokeDasharray="12 9" opacity="0.9" />
        {[[204, 42], [216, 36], [228, 30], [212, 48]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2.5 + (i % 2)} fill="#db2777" className="fest-spark" style={{ animationDelay: `${i * 0.3}s` }} />
        ))}
      </g>
      <GulalPuff x={40} y={60} color="#22c55e" delay={0.8} />
    </g>
  </svg>
);

/* ---------------- DIWALI ---------------- */
const StringLights = () => (
  <svg className="fest-lights" viewBox="0 0 1200 60" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 8 Q150 50 300 8 T600 8 T900 8 T1200 8" fill="none" stroke="#1f2937" strokeWidth="2" />
    {Array.from({ length: 30 }).map((_, i) => {
      const x = 20 + i * 40;
      const seg = (x % 300) / 300;
      const y = 8 + Math.sin(seg * Math.PI) * 42;
      const colors = ['#fbbf24', '#f472b6', '#34d399', '#60a5fa', '#fb923c'];
      const c = colors[i % colors.length];
      return (
        <g key={i} transform={`translate(${x} ${y})`}>
          <rect x="-2.5" y="-2" width="5" height="6" fill="#374151" />
          <circle cy="10" r="6" fill={c} className="fest-twinkle" style={{ animationDelay: `${(i % 5) * 0.3}s` }} />
          <circle cy="10" r="10" fill={c} opacity="0.25" className="fest-twinkle" style={{ animationDelay: `${(i % 5) * 0.3}s` }} />
        </g>
      );
    })}
  </svg>
);

const Rocket = ({ className, delay = 0, color = '#f472b6' }) => (
  <div className={`fest-rocket ${className}`} aria-hidden="true">
    <svg className="fest-rocket-body" style={{ animationDelay: `${delay}s` }} viewBox="0 0 40 120" width="40" height="120">
      <path d="M20 4 L28 26 H12 Z" fill="#dc2626" />
      <rect x="13" y="26" width="14" height="40" rx="3" fill="#fbbf24" />
      <rect x="13" y="26" width="14" height="40" rx="3" fill="none" stroke="#b45309" strokeWidth="1.5" />
      <path d="M12 66 L8 80 H32 L28 66 Z" fill="#b91c1c" />
      <rect x="19" y="80" width="2" height="36" fill="#78350f" />
      <path d="M20 118 C24 108 30 100 20 92 C10 100 16 108 20 118 Z" fill="#fb923c" className="fest-flame" />
    </svg>
    <div className="fest-burst" style={{ '--burst-color': color }}>
      {Array.from({ length: 12 }).map((_, i) => <span key={i} style={{ '--i': i, animationDelay: `${delay}s` }} />)}
    </div>
  </div>
);

const DiyaRow = () => (
  <svg className="fest-diyas" viewBox="0 0 1200 60" preserveAspectRatio="none" aria-hidden="true">
    {Array.from({ length: 14 }).map((_, i) => {
      const x = 45 + i * 86;
      return (
        <g key={i} transform={`translate(${x} 0)`}>
          <ellipse cx="0" cy="44" rx="22" ry="14" fill="#f59e0b" opacity="0.15" className="fest-glow" />
          <path className="fest-flame" style={{ transformOrigin: `${x}px 40px` }} d="M0 14 C5 22 8 26 6 32 C4 37 -4 37 -6 32 C-8 26 -5 22 0 14 Z" fill="#fbbf24" />
          <ellipse cx="0" cy="40" rx="16" ry="5" fill="#c2410c" />
          <path d="M-16 40 L-12 50 Q0 54 12 50 L16 40 Z" fill="#9a3412" />
          <path d="M-12 50 Q0 54 12 50" fill="none" stroke="#fbbf24" strokeWidth="1.5" />
        </g>
      );
    })}
  </svg>
);

const Rangoli = ({ className }) => (
  <svg className={className} viewBox="0 0 200 200" aria-hidden="true">
    <g className="fest-spin" style={{ transformOrigin: '100px 100px' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <g key={i} transform={`rotate(${i * 45} 100 100)`}>
          <ellipse cx="100" cy="46" rx="14" ry="30" fill={i % 2 ? '#f472b6' : '#fb923c'} opacity="0.85" />
          <ellipse cx="100" cy="52" rx="7" ry="18" fill="#fde047" opacity="0.9" />
          <circle cx="100" cy="12" r="6" fill={i % 2 ? '#34d399' : '#60a5fa'} />
        </g>
      ))}
      <circle cx="100" cy="100" r="22" fill="#dc2626" />
      <circle cx="100" cy="100" r="12" fill="#fde047" />
    </g>
  </svg>
);

/* Lit sparkler (phuljhadi): wire + radiating sparks that twinkle */
const Sparkler = ({ x, y, angle = -60, length = 40, color = '#fde68a' }) => {
  const rad = (angle * Math.PI) / 180;
  const tx = x + Math.cos(rad) * length;
  const ty = y + Math.sin(rad) * length;
  return (
    <g>
      <path d={`M${x} ${y} L${tx} ${ty}`} stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
      <g className="fest-sparkler" style={{ transformOrigin: `${tx}px ${ty}px` }}>
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const l = i % 2 ? 9 : 15;
          return <path key={i} d={`M${tx} ${ty} L${tx + Math.cos(a) * l} ${ty + Math.sin(a) * l}`} stroke={i % 3 ? color : '#fff7ed'} strokeWidth="1.6" strokeLinecap="round" />;
        })}
        <circle cx={tx} cy={ty} r="3.5" fill="#fff7ed" />
      </g>
      {[[-14, -10], [12, -14], [16, 8], [-10, 14]].map(([dx, dy], i) => (
        <circle key={i} cx={tx + dx} cy={ty + dy} r="1.6" fill={color} className="fest-spark" style={{ animationDelay: `${i * 0.35}s` }} />
      ))}
    </g>
  );
};

/* Small lit diya held in hands */
const HandDiya = ({ x, y }) => (
  <g>
    <ellipse cx={x} cy={y} rx="16" ry="12" fill="#f59e0b" opacity="0.22" className="fest-glow" />
    <path className="fest-flame" style={{ transformOrigin: `${x}px ${y - 2}px` }} d={`M${x} ${y - 16} C${x + 4} ${y - 10} ${x + 6} ${y - 7} ${x + 4} ${y - 3} C${x + 2} ${y} ${x - 2} ${y} ${x - 4} ${y - 3} C${x - 6} ${y - 7} ${x - 4} ${y - 10} ${x} ${y - 16} Z`} fill="#fbbf24" />
    <ellipse cx={x} cy={y - 1} rx="10" ry="3" fill="#c2410c" />
    <path d={`M${x - 10} ${y - 1} L${x - 7} ${y + 6} Q${x} ${y + 9} ${x + 7} ${y + 6} L${x + 10} ${y - 1} Z`} fill="#9a3412" />
    <path d={`M${x - 7} ${y + 6} Q${x} ${y + 9} ${x + 7} ${y + 6}`} fill="none" stroke={GOLD} strokeWidth="1.2" />
  </g>
);

/* Diwali mother in a teal saree with gold border, gajra in her bun, lighting/holding a diya; child beside her with a sparkler */
const DiwaliMotherChild = ({ className }) => (
  <svg className={className} viewBox="0 0 200 230" aria-hidden="true">
    <defs>
      <linearGradient id="sareeGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0f766e" />
        <stop offset="100%" stopColor="#115e59" />
      </linearGradient>
    </defs>
    {/* ---- mother ---- */}
    <g className="fest-dance">
      {/* pallu over the shoulder falling behind */}
      <path d="M96 58 C112 80 118 120 112 160 C110 180 118 200 112 216 C100 200 96 180 98 160 C102 120 92 90 74 66 Z" fill="url(#sareeGrad)" opacity="0.95" />
      <path d="M112 160 C110 180 118 200 112 216" fill="none" stroke={GOLD} strokeWidth="2.5" />
      {/* saree skirt with pleats and gold border */}
      <path d="M66 96 Q84 92 102 96 L112 212 Q84 220 56 212 Z" fill="url(#sareeGrad)" />
      {[72, 80, 88, 96].map((x, i) => <path key={i} d={`M${x} 110 L${x + (x - 84) * 0.3} 208`} stroke="#0d9488" strokeWidth="1.2" opacity="0.7" />)}
      <path d="M56 212 Q84 220 112 212" fill="none" stroke={GOLD} strokeWidth="5" />
      <path d="M58 202 Q84 210 110 202" fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="1 4" />
      {/* blouse + midriff */}
      <path d="M66 60 Q84 66 102 60 L104 78 Q84 84 64 78 Z" fill={MAROON} />
      <path d="M66 60 Q84 66 102 60" fill="none" stroke={GOLD} strokeWidth="2" />
      <path d="M68 78 Q84 84 100 78 L102 96 Q84 92 66 96 Z" fill={SKIN} />
      {/* saree drape across the front */}
      <path d="M100 62 C88 76 74 84 66 96 L102 96 Z" fill="url(#sareeGrad)" />
      <path d="M100 62 C88 76 74 84 66 96" fill="none" stroke={GOLD} strokeWidth="2" />
      {/* neck + head, bun with gajra, tikka, earrings, necklace */}
      <rect x="78" y="44" width="12" height="16" rx="5" fill={SKIN} />
      <circle cx="84" cy="34" r="13" fill={SKIN} />
      <circle cx="98" cy="30" r="6.5" fill={SKIN} />
      {[[94, 25], [99, 23], [104, 27], [103, 33]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.8" fill="#fff7ed" />)}
      <circle cx="84" cy="25" r="1.8" fill={GOLD} />
      <circle cx="73" cy="36" r="1.7" fill={GOLD} />
      <path d="M76 58 Q84 64 92 58" fill="none" stroke={GOLD} strokeWidth="2" />
      {/* arms cupping a diya at chest height */}
      <path d="M66 66 C56 80 58 92 70 98" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M102 66 C112 80 110 92 98 98" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {[[60, 84], [62, 89]].map(([x, y], i) => <path key={i} d={`M${x - 4} ${y - 2} L${x + 4} ${y + 2}`} stroke={i ? RED : GOLD} strokeWidth="2.5" strokeLinecap="round" />)}
      {[[108, 84], [106, 89]].map(([x, y], i) => <path key={i} d={`M${x - 4} ${y + 2} L${x + 4} ${y - 2}`} stroke={i ? RED : GOLD} strokeWidth="2.5" strokeLinecap="round" />)}
      <HandDiya x={84} y={98} />
    </g>
    {/* ---- child ---- */}
    <g className="fest-dance-rev" style={{ transformOrigin: '150px 216px' }}>
      <path d="M144 176 L140 210 M156 176 L160 210" stroke="#7c2d12" strokeWidth="9" strokeLinecap="round" />
      <path d="M134 214 Q140 208 148 212 L148 216 Z M152 212 Q160 208 166 214 L166 216 Z" fill={RED} />
      {/* kurta */}
      <path d="M136 132 Q150 136 164 132 L170 178 Q150 184 130 178 Z" fill="#facc15" />
      <path d="M130 178 Q150 184 170 178" fill="none" stroke={RED} strokeWidth="2.5" />
      <path d="M150 134 L150 156" stroke={RED} strokeWidth="1.5" />
      <circle cx="150" cy="140" r="1.3" fill={RED} /><circle cx="150" cy="147" r="1.3" fill={RED} /><circle cx="150" cy="154" r="1.3" fill={RED} />
      {/* head with side-parted hair, big smile */}
      <rect x="145" y="120" width="10" height="12" rx="4" fill={SKIN} />
      <circle cx="150" cy="114" r="10" fill={SKIN} />
      <path d="M140 110 C142 100 158 100 160 110 Q150 106 140 110 Z" fill="#111827" />
      <path d="M145 118 Q150 122 155 118" fill="none" stroke="#fde68a" strokeWidth="1.4" strokeLinecap="round" />
      {/* arms: one up with sparkler, one holding mother's saree */}
      <path d="M162 138 C170 128 176 118 180 106" fill="none" stroke={SKIN} strokeWidth="5.5" strokeLinecap="round" />
      <path d="M138 138 C128 136 120 128 114 118" fill="none" stroke={SKIN} strokeWidth="5.5" strokeLinecap="round" />
      <Sparkler x={180} y={106} angle={-75} length={34} />
    </g>
  </svg>
);

/* Diwali father in a maroon bandhgala kurta with gold buttons, holding a thali of diyas and a sparkler */
const DiwaliFather = ({ className }) => (
  <svg className={className} viewBox="0 0 170 230" aria-hidden="true">
    <g className="fest-dance-rev">
      {/* churidar legs + mojari */}
      <path d="M74 150 L68 208" stroke="#f1e4c8" strokeWidth="16" strokeLinecap="round" />
      <path d="M98 150 L104 208" stroke="#f1e4c8" strokeWidth="16" strokeLinecap="round" />
      <path d="M68 208 L66 216 M104 208 L106 216" stroke={SKIN} strokeWidth="9" strokeLinecap="round" />
      <path d="M52 220 Q62 212 76 218 L76 224 Z M96 218 Q110 212 120 220 L120 224 Z" fill={GOLD} />
      {/* kurta */}
      <path d="M60 60 Q86 66 112 60 C114 84 110 100 114 118 L120 156 Q86 166 52 156 L58 118 C62 100 58 84 60 60 Z" fill={MAROON} />
      <path d="M52 156 Q86 166 120 156" fill="none" stroke={GOLD} strokeWidth="3" />
      <path d="M60 60 L48 82 L62 88 Z M112 60 L124 82 L110 88 Z" fill={MAROON} />
      <path d="M48 82 L62 88 M124 82 L110 88" stroke={GOLD} strokeWidth="2" />
      <path d="M86 62 L86 124" stroke={GOLD} strokeWidth="1.5" />
      {[72, 84, 96, 108, 120].map((y, i) => <circle key={i} cx="86" cy={y} r="1.8" fill={GOLD} />)}
      <path d="M74 60 Q86 65 98 60" fill="none" stroke={GOLD} strokeWidth="2.5" />
      {/* neck + head, neat hair, tilak */}
      <rect x="79" y="46" width="14" height="16" rx="6" fill={SKIN} />
      <circle cx="86" cy="34" r="13" fill={SKIN} />
      <path d="M73 30 C75 16 97 16 99 30 Q86 26 73 30 Z" fill="#111827" />
      <path d="M86 24 L86 30" stroke={RED} strokeWidth="2" strokeLinecap="round" />
      {/* arms: left holds thali of diyas, right holds sparkler high */}
      <path d="M64 68 C54 82 48 96 44 108" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M108 66 C120 56 128 48 132 40" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {/* thali with three small diyas */}
      <ellipse cx="40" cy="112" rx="24" ry="7" fill={GOLD} />
      <ellipse cx="40" cy="111" rx="20" ry="5" fill="#fde68a" />
      <HandDiya x={28} y={104} />
      <HandDiya x={42} y={102} />
      <HandDiya x={54} y={105} />
      <Sparkler x={132} y={40} angle={-72} length={26} />
    </g>
  </svg>
);

/* ---------------- UTTARAYAN (kite festival) ---------------- */
const KITE_COLORS = [['#ef4444', '#facc15'], ['#3b82f6', '#f8fafc'], ['#22c55e', '#fde047'], ['#a855f7', '#f472b6'], ['#f97316', '#0ea5e9'], ['#ec4899', '#fef3c7']];

/* Patang: diamond kite with a contrasting half, spine/bow sticks and a fluttering tail */
const Kite = ({ className, colors, delay = 0, duration = 26, top = '14%', size = 70 }) => {
  const [a, b] = colors;
  return (
    <div className={`fest-kite ${className || ''}`} style={{ top, width: size, height: size * 1.9, animationDelay: `${delay}s`, animationDuration: `${duration}s` }} aria-hidden="true">
      <svg viewBox="0 0 70 134" width={size} height={size * 1.9} className="fest-kite-bob" style={{ animationDelay: `${delay * 0.37}s` }}>
        <path d="M35 2 L68 42 L35 88 Z" fill={a} />
        <path d="M35 2 L2 42 L35 88 Z" fill={b} />
        <path d="M35 2 L68 42 L35 88 L2 42 Z" fill="none" stroke="#1f2937" strokeWidth="1.2" />
        <path d="M35 2 L35 88 M2 42 Q35 30 68 42" fill="none" stroke="#7c2d12" strokeWidth="1.6" />
        <circle cx="35" cy="42" r="4" fill="#fff7ed" stroke="#1f2937" strokeWidth="0.8" />
        {/* tail */}
        <path className="fest-kite-tail" d="M35 88 C42 98 28 108 36 118 C42 126 30 130 34 134" fill="none" stroke={a} strokeWidth="3" strokeLinecap="round" />
        <path className="fest-kite-tail" d="M35 88 C42 98 28 108 36 118 C42 126 30 130 34 134" fill="none" stroke={b} strokeWidth="1.2" strokeDasharray="4 6" />
        {/* string trailing down-left */}
        <path d="M35 42 C20 70 -40 120 -120 170" fill="none" stroke="#9ca3af" strokeWidth="0.8" opacity="0.7" />
      </svg>
    </div>
  );
};

/* Firki (thread spool) — bright wooden reel with wound manja thread */
const Firki = ({ x, y, color, thread = '#f472b6' }) => (
  <g transform={`translate(${x} ${y})`}>
    <path d="M0 -20 L0 -8" stroke="#374151" strokeWidth="1.2" />
    <rect x="-16" y="-8" width="32" height="6" rx="2" fill={color} />
    <rect x="-16" y="22" width="32" height="6" rx="2" fill={color} />
    <rect x="-11" y="-2" width="22" height="24" rx="2" fill={thread} />
    {[2, 6, 10, 14, 18].map((yy, i) => <path key={i} d={`M-11 ${yy} L11 ${yy + 1.5}`} stroke="#fff" strokeWidth="0.8" opacity="0.5" />)}
    <rect x="-22" y="6" width="44" height="4" rx="2" fill="#78350f" />
    <circle cx="-22" cy="8" r="3" fill="#fbbf24" /><circle cx="22" cy="8" r="3" fill="#fbbf24" />
  </g>
);

const FirkiBunting = () => (
  <svg className="fest-toran" viewBox="0 0 1200 70" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 6 Q300 52 600 6 T1200 6" fill="none" stroke="#374151" strokeWidth="1.6" />
    {Array.from({ length: 16 }).map((_, i) => {
      const x = 38 + i * 75;
      const t = (x / 1200) * Math.PI * 2;
      const y = 6 + Math.abs(Math.sin(t)) * 42;
      const [c, th] = KITE_COLORS[i % KITE_COLORS.length];
      return <Firki key={i} x={x} y={y + 18} color={c} thread={th} />;
    })}
  </svg>
);

const WinterSun = ({ className }) => (
  <svg className={className} viewBox="0 0 160 160" aria-hidden="true">
    <g className="fest-spin" style={{ transformOrigin: '80px 80px', animationDuration: '90s' }}>
      {Array.from({ length: 16 }).map((_, i) => (
        <path key={i} d="M80 6 L84 30 L76 30 Z" fill={GOLD} opacity={i % 2 ? 0.55 : 0.9} transform={`rotate(${i * 22.5} 80 80)`} />
      ))}
    </g>
    <circle cx="80" cy="80" r="44" fill="#fde68a" opacity="0.5" className="fest-glow" />
    <circle cx="80" cy="80" r="32" fill="#fbbf24" />
    <circle cx="80" cy="80" r="26" fill="#fcd34d" />
  </svg>
);

/* Kite-flyer on a terrace: child in a woolly sweater & topi leaning back, holding the manja; friend with the firki */
const KiteFlyers = ({ className }) => (
  <svg className={className} viewBox="0 0 260 230" aria-hidden="true">
    {/* terrace parapet */}
    <rect x="0" y="196" width="260" height="34" fill="#b45309" />
    <rect x="0" y="190" width="260" height="8" fill="#d97706" />
    {[10, 50, 90, 130, 170, 210].map((x, i) => <rect key={i} x={x} y="204" width="30" height="14" rx="1" fill="#f59e0b" opacity="0.6" />)}
    {/* ---- flyer (leaning back, both hands on the string) ---- */}
    <g className="fest-lean" style={{ transformOrigin: '96px 196px' }}>
      <path d="M86 150 L80 194 M104 150 L112 194" stroke="#1e3a8a" strokeWidth="13" strokeLinecap="round" />
      <path d="M70 200 Q80 192 92 198 L92 202 Z M104 198 Q116 192 126 200 L126 202 Z" fill="#111827" />
      <path d="M76 84 Q96 90 116 84 L124 152 Q96 160 68 152 Z" fill={RED} />
      <path d="M68 152 Q96 160 124 152" fill="none" stroke="#fde68a" strokeWidth="3" />
      <path d="M72 108 Q96 116 120 108 M72 124 Q96 132 120 124" fill="none" stroke="#fde68a" strokeWidth="2" strokeDasharray="4 3" />
      <rect x="89" y="68" width="14" height="18" rx="6" fill={SKIN} />
      <circle cx="96" cy="58" r="13" fill={SKIN} />
      <path d="M82 54 C84 40 108 40 110 54 Q96 50 82 54 Z" fill="#facc15" />
      <circle cx="96" cy="40" r="4" fill={RED} />
      <path d="M90 62 Q96 67 102 62" fill="none" stroke="#fde68a" strokeWidth="1.5" strokeLinecap="round" />
      {/* arms up-right holding the string */}
      <path d="M116 90 C130 76 142 64 154 54" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      <path d="M78 92 C96 80 120 70 140 60" fill="none" stroke={SKIN} strokeWidth="7" strokeLinecap="round" />
      {/* manja rising to the sky */}
      <path d="M154 54 C190 30 230 10 270 -20" fill="none" stroke="#9ca3af" strokeWidth="1.2" />
    </g>
    {/* ---- friend holding the firki ---- */}
    <g className="fest-dance-rev" style={{ transformOrigin: '190px 196px' }}>
      <path d="M182 156 L178 194 M198 156 L204 194" stroke="#065f46" strokeWidth="12" strokeLinecap="round" />
      <path d="M168 200 Q178 192 190 198 L190 202 Z M198 198 Q210 192 220 200 L220 202 Z" fill="#111827" />
      <path d="M174 100 Q190 106 206 100 L212 158 Q190 166 168 158 Z" fill="#0ea5e9" />
      <path d="M168 158 Q190 166 212 158" fill="none" stroke="#fef3c7" strokeWidth="3" />
      <rect x="184" y="86" width="12" height="16" rx="5" fill={SKIN} />
      <circle cx="190" cy="76" r="11" fill={SKIN} />
      <path d="M178 74 C180 62 200 62 202 74 Q190 70 178 74 Z" fill="#111827" />
      <path d="M180 73 L200 73" stroke="#f472b6" strokeWidth="3" />
      <path d="M185 80 Q190 84 195 80" fill="none" stroke="#fde68a" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M176 106 C168 116 160 124 152 130" fill="none" stroke={SKIN} strokeWidth="6" strokeLinecap="round" />
      <path d="M204 106 C210 116 214 124 216 132" fill="none" stroke={SKIN} strokeWidth="6" strokeLinecap="round" />
      <g transform="translate(150 132) rotate(-20)">
        <rect x="-18" y="-6" width="36" height="6" rx="2" fill="#dc2626" />
        <rect x="-18" y="18" width="36" height="6" rx="2" fill="#dc2626" />
        <rect x="-12" y="0" width="24" height="18" rx="2" fill="#f472b6" />
        <rect x="-24" y="6" width="48" height="4" rx="2" fill="#78350f" />
      </g>
      <path d="M150 128 C150 100 152 76 154 54" fill="none" stroke="#9ca3af" strokeWidth="0.9" opacity="0.7" />
    </g>
  </svg>
);

/* ---------------- NATIONAL ---------------- */
const Bunting = ({ colors }) => (
  <svg className="fest-toran" viewBox="0 0 1200 60" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 4 Q300 44 600 4 T1200 4" fill="none" stroke="#374151" strokeWidth="2" />
    {Array.from({ length: 30 }).map((_, i) => {
      const x = 20 + i * 40;
      const t = (x / 1200) * Math.PI * 2;
      const y = 4 + Math.abs(Math.sin(t)) * 38;
      return <path key={i} d={`M${x - 14} ${y} L${x} ${y + 26} L${x + 14} ${y} Z`} fill={colors[i % colors.length]} />;
    })}
  </svg>
);

export const FestivalDecor = ({ theme, hideDancers = false }) => {
  if (!theme || theme === 'default') return null;

  if (theme === 'navratri') {
    return (
      <div className="fest-layer" data-testid="festival-decor-navratri" aria-hidden="true">
        <Toran />
        {!hideDancers && <>
          <DancerChaniya className="fest-fixed fest-dancer-left" />
          <DancerKediyu className="fest-fixed fest-dancer-right" />
        </>}
        <Garbo className="fest-fixed fest-garbo" />
        <Trishul className="fest-fixed fest-trishul" />
        <Dandiya className="fest-fixed fest-dandiya-a" />
        <Dandiya className="fest-fixed fest-dandiya-b" flip />
      </div>
    );
  }
  if (theme === 'holi') {
    return (
      <div className="fest-layer" data-testid="festival-decor-holi" aria-hidden="true">
        <div className="fest-gulal fest-gulal-1" /><div className="fest-gulal fest-gulal-2" /><div className="fest-gulal fest-gulal-3" />
        <div className="fest-gulal fest-gulal-4" /><div className="fest-gulal fest-gulal-5" />
        {Array.from({ length: 14 }).map((_, i) => <span key={i} className="fest-speck" style={{ '--i': i }} />)}
        <Bonfire className="fest-fixed fest-bonfire" />
        {!hideDancers && <>
          <HoliWoman className="fest-fixed fest-holi-woman" />
          <HoliMan className="fest-fixed fest-holi-man" />
        </>}
        <Pichkari className="fest-fixed fest-pichkari-b" color="#16a34a" flip />
        <Balloon className="fest-fixed fest-balloon-a" color="#a21caf" />
        <Balloon className="fest-fixed fest-balloon-b" color="#0ea5e9" />
        <Balloon className="fest-fixed fest-balloon-c" color="#facc15" />
      </div>
    );
  }
  if (theme === 'diwali') {
    return (
      <div className="fest-layer" data-testid="festival-decor-diwali" aria-hidden="true">
        <StringLights />
        <Rocket className="fest-rocket-a" delay={0} color="#f472b6" />
        <Rocket className="fest-rocket-b" delay={2.6} color="#fbbf24" />
        <Rocket className="fest-rocket-c" delay={5.1} color="#34d399" />
        <Rangoli className="fest-fixed fest-rangoli" />
        {!hideDancers && <>
          <DiwaliMotherChild className="fest-fixed fest-diwali-family" />
          <DiwaliFather className="fest-fixed fest-diwali-father" />
        </>}
        <DiyaRow />
      </div>
    );
  }
  if (theme === 'uttarayan') {
    return (
      <div className="fest-layer" data-testid="festival-decor-uttarayan" aria-hidden="true">
        <FirkiBunting />
        <WinterSun className="fest-fixed fest-sun" />
        <Kite className="fest-kite-1" colors={KITE_COLORS[0]} delay={0} duration={28} top="16%" size={72} />
        <Kite className="fest-kite-2" colors={KITE_COLORS[1]} delay={-9} duration={34} top="26%" size={56} />
        <Kite className="fest-kite-3" colors={KITE_COLORS[2]} delay={-18} duration={30} top="12%" size={48} />
        <Kite className="fest-kite-4" colors={KITE_COLORS[3]} delay={-4} duration={38} top="34%" size={40} />
        <Kite className="fest-kite-5" colors={KITE_COLORS[4]} delay={-24} duration={32} top="20%" size={60} />
        <Kite className="fest-kite-6" colors={KITE_COLORS[5]} delay={-13} duration={40} top="40%" size={34} />
        {!hideDancers && <KiteFlyers className="fest-fixed fest-kite-flyers" />}
      </div>
    );
  }
  if (theme === 'independence_day' || theme === 'republic_day') {
    return <div className="fest-layer" data-testid={`festival-decor-${theme}`} aria-hidden="true"><Bunting colors={['#f97316', '#ffffff', '#15803d']} /></div>;
  }
  if (theme === 'gujarat_day') {
    return <div className="fest-layer" data-testid="festival-decor-gujarat_day" aria-hidden="true"><Bunting colors={['#15803d', '#f97316', '#fbbf24']} /></div>;
  }
  return null;
};
